import { useTranslation } from 'react-i18next';
import { Button } from '@c2fo/liquidity';
import { BuyerCurrencyGroupWithStats } from '@/enterprise/components/InvoiceCentral/useGroupByBuyerCurrency';
import useAdjustmentInvoiceStats from '@/enterprise/data/useAdjustmentInvoiceStats';
import useLockedInvoiceStats from '@/enterprise/data/useLockedInvoiceStats';
import usePlayableInvoiceStats from '@/enterprise/data/usePlayableInvoiceStats';
import { useReporting } from '@/reporting';
import { ButtonLink } from '@/shared/components/ButtonLink';
import QueryBoundaries from '@/shared/components/QueryBoundaries';
import { LinkOption } from './InvoiceCentralLayout';
import useDownloadInvoicesUrl from './useDownloadInvoicesUrl';

interface DownloadInvoiceProps {
  makerOrganizationUuid: string;
  currentBuyer: BuyerCurrencyGroupWithStats;
  currency: string;
  currentTable: LinkOption;
}

const DownloadInvoicesComponent = ({
  currentBuyer,
  makerOrganizationUuid,
  currency,
  currentTable,
}: DownloadInvoiceProps) => {
  const { trackEnterpriseEvent } = useReporting();
  const { t } = useTranslation();

  const { data: eligibleInvoiceStats } = usePlayableInvoiceStats({
    makerOrganizationUuid,
    takerMarkets: currentBuyer.takerMarkets,
  });
  const { data: ineligibleInvoiceStats } = useLockedInvoiceStats({
    makerOrganizationUuid,
    takerMarkets: currentBuyer.takerMarkets,
  });
  const { data: adjustmentStats } = useAdjustmentInvoiceStats({
    makerOrganizationUuid,
    takerMarkets: currentBuyer.takerMarkets,
  });

  const disableDownload =
    (currentTable === 'eligible' && eligibleInvoiceStats?.totalCount === 0) ||
    (currentTable === 'ineligible' && ineligibleInvoiceStats?.totalCount === 0) ||
    (currentTable === 'adjustments' && adjustmentStats?.totalCount === 0);

  const downloadInvoicesUrl = useDownloadInvoicesUrl({
    makerOrganizationUuid,
    takerMarketIds: currentBuyer.takerMarkets.map((tm) => ({
      takerId: tm.takerDivisionId,
      marketId: tm.legacyMarketId,
    })),
    currency,
    type: currentTable ?? 'eligible',
  });

  return (
    <ButtonLink
      className="w-full md:w-fit"
      onClick={() =>
        trackEnterpriseEvent('invoice-central::download-invoices::submitted', {
          invoiceType: currentTable,
          // Array of ids used for the download endpoint
          takerMarketIds: currentBuyer.takerMarkets.map((tm) => ({
            takerId: tm.takerDivisionId,
            marketId: tm.legacyMarketId,
          })),
        })
      }
      disabled={disableDownload}
      variant="tertiary"
      to={downloadInvoicesUrl}
    >
      {t('core.downloadInvoices')}
    </ButtonLink>
  );
};

const DownloadInvoices = (props: DownloadInvoiceProps) => {
  const { t } = useTranslation();

  return (
    <QueryBoundaries
      LoadingComponent={() => (
        <Button className="w-full md:w-fit" disabled variant="tertiary">
          {t('core.downloadInvoices')}
        </Button>
      )}
      ErrorComponent={() => (
        <Button className="w-full md:w-fit" disabled variant="tertiary">
          {t('core.downloadInvoices')}
        </Button>
      )}
    >
      <DownloadInvoicesComponent {...props} />
    </QueryBoundaries>
  );
};

export default DownloadInvoices;
